import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import {
    Alert,
    Button,
    Divider,
    Flex,
    Group,
    InputWrapper,
    Notification,
    Paper,
    PasswordInput,
    Select,
    Stack,
    Switch,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import * as z from 'zod';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { usePostHealthcareProfessional } from '../../../global/entities/healthcareProfessional/mutations/usePostHealthcareProfessionalMutations.tsx';
import { useGetUserPreferences } from '../../../global/entities/UserConsent/queries/useGetUserPreferences.tsx';
import { useUpdateAlertBellMinSeverity } from '../../../global/entities/UserConsent/mutations/useUpdateAlertBellMinSeverity.tsx';
import {
    IFacilitySelection,
    IHealthcareProfessionalForm,
} from '../../../global/entities/healthcareProfessional/HealthcareProfessional.ts';
import { IconArrowLeft, IconAt, IconInfoCircle, IconLock, IconNumber, IconPhone } from '@tabler/icons-react';
import { useGetHealthcareProfessionalById } from '../../../global/entities/healthcareProfessional/queries/useGetHealthcareProfessionalById.tsx';
import { usePutHealthcareProfessional } from '../../../global/entities/healthcareProfessional/mutations/usePutHealthcareProfessionalMutations.tsx';
import { useNavigate } from 'react-router-dom';
import { AppMedError } from '../../../global/error/AppMedError.ts';
import { theme } from '../../../global/style/theme.tsx';
import FacilityMultiSelect from '../../../global/component/organism/MultiSelectModalHealthCareAssistant/FacilityMultiSelect.tsx';
import { useSessionProvider } from '../../../global/provider/sessionContext.tsx';
import { useGetHealthcareProfessionalNew } from '../../../global/entities/healthcareProfessional/queries/useGetHealthcareProfessionalNew.tsx';
import Utils from '../../../global/helpers/utils.ts';

const HcpForm: React.FC = inject()(
    observer(() => {
        const { t } = useTranslation();
        const params = useParams<{ id: string; tabId: string }>();
        const postHealthcareProfessional = usePostHealthcareProfessional();
        const putHealthcareProfessional = usePutHealthcareProfessional();
        const { data: hcpData } = useGetHealthcareProfessionalById();
        const { data: hcpNew } = useGetHealthcareProfessionalNew();

        const [error, setError] = useState<string | null>(null);
        const [isSubmitting, setIsSubmitting] = useState(false);
        const navigate = useNavigate();
        const { currentUser } = useSessionProvider();
        const isHcp = !!currentUser?.healthcareProfessionalID;
        const isOwnProfile = isHcp && params.id === currentUser?.healthcareProfessionalID?.toString();
        const { data: userPreferences } = useGetUserPreferences(isOwnProfile);
        const updateAlertBellMinSeverity = useUpdateAlertBellMinSeverity();

        const schema = z
            .object({
                healthcareProfessionalFacilities: z
                    .array(
                        z.object({
                            healthcareFacilityID: z.string().min(1, { message: t('formError.facilityIsRequired') }),
                        }),
                    )
                    .min(1, { message: t('formError.atLeastOneFacilityRequired') }),
                firstName: z.string().min(1, { message: t('formError.firstNameIsRequired') }),
                lastName: z.string().min(1, { message: t('formError.lastNameIsRequired') }),
                preferredLanguage: z.preprocess(
                    (val) => (val === null ? '' : val),
                    z.string().min(1, { message: t('formError.languageIsRequired') }),
                ),

                healthcareProfessionalTypeID: z.preprocess(
                    (val) => (val === null ? '' : val),
                    z.string().min(1, { message: t('formError.roleIsRequired') }),
                ),
                email: z.string().email({ message: t('formError.invalidEmailAddress') }),
                ...(params.id !== '0'
                    ? {}
                    : {
                          password: z
                              .string()
                              .min(8, { message: t('formError.passwordMustBeAtLeast8CharactersLong') })
                              .regex(/[a-z]/, { message: t('formError.passwordMustContainLowercase') })
                              .regex(/[A-Z]/, { message: t('formError.passwordMustContainUppercase') })
                              .regex(/[#!?@$%^&*\-_]/, { message: t('formError.passwordMustContainSpecialCharacter') }),
                          confirmPassword: z.string().min(1, { message: t('formError.confirmPasswordIsRequired') }),
                      }),
            })
            .refine(
                (data) => {
                    if (data.password || data.confirmPassword) 
                        return data.password === data.confirmPassword;
                    
                    return true;
                },
                {
                    message: t('formError.passwordsMustMatch'),
                    path: ['confirmPassword'],
                },
            );

        const form = useForm<IHealthcareProfessionalForm>({
            mode: 'controlled',
            initialValues: {
                id: null,
                titleID: null,
                firstName: '',
                lastName: '',
                preferredLanguage: null,
                healthcareProfessionalTypeID: null,
                email: '',
                password: '',
                confirmPassword: '',
                licenseNumber: '',
                phoneNumber: '',
                allowSMS: false,
                healthcareProfessionalFacilities: [],
            },
            validate: zodResolver(schema),
        });

        const handleCancel = () => {
            form.reset();
            navigate(isHcp ? 'hcpDashboard' : '/adminDashboard/hcp');
        };

        const handleSubmit = async (formValues: IHealthcareProfessionalForm) => {
            setError(null);
            setIsSubmitting(true);
            try {
                await (params.id !== '0' ? putHealthcareProfessional : postHealthcareProfessional).mutateAsync({
                    username: formValues.email.trim(),
                    password: formValues.password.trim(),
                    email: formValues.email.trim(),
                    firstName: formValues.firstName.trim(),
                    lastName: formValues.lastName.trim(),
                    preferredLanguage: formValues.preferredLanguage!,
                    titleID: Number(formValues.titleID) === 0 ? null : Number(formValues.titleID),
                    healthcareProfessionalTypeID: Number(formValues.healthcareProfessionalTypeID),
                    licenseNumber: formValues.licenseNumber.trim(),
                    phoneNumber: formValues.phoneNumber?.trim() || undefined,
                    allowSMS: formValues.allowSMS,
                    healthcareProfessionalFacilities: formValues.healthcareProfessionalFacilities.map(
                        (facility: IFacilitySelection) => ({
                            healthcareFacilityID: Number(facility.healthcareFacilityID),
                            assistant: facility.assistant,
                            assistantEmail: facility.assistantEmail?.trim() === '' ? null : facility.assistantEmail,
                        }),
                    ),
                });
            } catch (e) {
                if (e instanceof AppMedError) 
                    setError(e.message);
                 else 
                    setError('An unexpected error occurred.');
                
            } finally {
                setIsSubmitting(false);
                handleCancel();
            }
        };

        useEffect(() => {
            if (hcpData && form.values.id !== hcpData.id) {
                form.reset();
                form.setValues({
                    titleID: hcpData.titleID?.toString(),
                    firstName: hcpData.firstName,
                    lastName: hcpData.lastName,
                    preferredLanguage: hcpData.preferredLanguage != null ? hcpData.preferredLanguage : '',
                    healthcareProfessionalTypeID: hcpData.healthcareProfessionalTypeID.toString(),
                    email: hcpData.email,
                    phoneNumber: hcpData.phoneNumber || '',
                    allowSMS: hcpData.allowSMS || false,
                    licenseNumber: hcpData.licenseNumber,
                    healthcareProfessionalFacilities: hcpData.healthcareProfessionalFacilities.map(
                        (facility: IFacilitySelection) => ({
                            healthcareFacilityID: facility.healthcareFacilityID?.toString(),
                            assistant: facility.assistant,
                            assistantEmail: facility.assistantEmail,
                        }),
                    ),
                });
            }
        }, [hcpData, params.id]);

        return (
            <>
                <Flex mb={'lg'} mt={'lg'} align={'center'} justify={'space-between'}>
                    <Button
                        leftSection={<IconArrowLeft color={theme.colors?.blue?.toString()} />}
                        onClick={() => navigate(isHcp ? 'hcpDashboard' : '/adminDashboard/hcp')}
                        variant="transparent"
                    >
                        <Title c="black" order={4}>
                            {`${hcpData?.firstName || `${hcpData?.name || t('hcp.addNewHCP')}`} ${hcpData?.lastName || ''}`}
                        </Title>
                    </Button>
                </Flex>
                <Paper>
                    <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
                        <Stack>
                            <Group grow>
                                <Select
                                    label={t('formGlobal.title')}
                                    placeholder={t('formGlobal.selectTitle')}
                                    data={hcpNew?.titlesCombobox}
                                    {...form.getInputProps('titleID')}
                                />
                                <TextInput
                                    label={t('formGlobal.firstName')}
                                    placeholder={t('formGlobal.enterFirstName')}
                                    {...form.getInputProps('firstName')}
                                />
                                <TextInput
                                    label={t('formGlobal.lastName')}
                                    placeholder={t('formGlobal.enterLastName')}
                                    {...form.getInputProps('lastName')}
                                />
                            </Group>
                            <Group grow>
                                <Select
                                    label={t('formGlobal.language')}
                                    placeholder={t('formGlobal.selectLanguage')}
                                    data={[
                                        { value: 'en', label: t('English') },
                                        { value: 'fr', label: t('French') },
                                    ]}
                                    {...form.getInputProps('preferredLanguage')}
                                />
                                <Select
                                    label={t('formGlobal.role')}
                                    placeholder={t('formGlobal.selectRole')}
                                    data={hcpNew?.healthcareProfessionalTypesCombobox}
                                    disabled={isHcp}
                                    {...form.getInputProps('healthcareProfessionalTypeID')}
                                />
                            </Group>
                            <Group grow align="flex-start">
                                <Stack gap="xs">
                                    <TextInput
                                        label={t('formProfile.phoneNumber')}
                                        placeholder="(415) 555-1212"
                                        leftSection={<IconPhone size={16} />}
                                        {...form.getInputProps('phoneNumber')}
                                        onBlur={(e) => form.setFieldValue('phoneNumber', Utils.formatPhoneNumber(e.currentTarget.value))}
                                    />
                                    {hcpData?.smsOptOutSource === 1 ? (
                                        <Alert variant="light" color="orange" icon={<IconInfoCircle />}>
                                            <Trans
                                                i18nKey="formProfile.smsOptOutByStopWord"
                                                values={{ phone: hcpData.smsPhoneNumber }}
                                                components={{
                                                    1: <a href={`sms:${hcpData.smsPhoneNumber}?body=START`} style={{ fontWeight: 600 }} />,
                                                    2: <strong />,
                                                }}
                                            />
                                        </Alert>
                                    ) : (
                                        <Switch
                                            label={t('formProfile.allowSmsNotifications')}
                                            disabled={!form.values.phoneNumber}
                                            checked={form.values.allowSMS}
                                            onChange={(event) => form.setFieldValue('allowSMS', event.currentTarget.checked)}
                                        />
                                    )}
                                </Stack>
                                <TextInput
                                    label={t('formGlobal.email')}
                                    placeholder={t('formGlobal.enterEmailAddress')}
                                    leftSection={<IconAt size={16} />}
                                    {...form.getInputProps('email')}
                                />
                            </Group>
                            {params.id === '0' && !isHcp && (
                                <Group grow>
                                    <PasswordInput
                                        label={t('formGlobal.password')}
                                        placeholder={t('formGlobal.enterPassword')}
                                        leftSection={<IconLock size={16} />}
                                        {...form.getInputProps('password')}
                                    />
                                    <PasswordInput
                                        label={t('formGlobal.confirmPassword')}
                                        placeholder={t('formGlobal.confirmPassword')}
                                        leftSection={<IconLock size={16} />}
                                        {...form.getInputProps('confirmPassword')}
                                    />
                                </Group>
                            )}
                            <Group grow>
                                <TextInput
                                    label={t('formGlobal.licenseNumber')}
                                    leftSection={<IconNumber size={16} />}
                                    maxLength={100}
                                    {...form.getInputProps('licenseNumber')}
                                />
                                <InputWrapper
                                    size={'sm'}
                                    styles={{ label: { fontSize: 16, fontWeight: 'bold' } }}
                                    label={t('formHcf.affiliatedHealthFacility')}
                                    error={form.errors.healthcareProfessionalFacilities}
                                >
                                    <FacilityMultiSelect
                                        value={form.values.healthcareProfessionalFacilities}
                                        onChange={(values) =>
                                            form.setFieldValue('healthcareProfessionalFacilities', values)
                                        }
                                        isError={!!form.errors.healthcareProfessionalFacilities}
                                        hcfData={hcpNew?.healthcareFacilities}
                                    />
                                </InputWrapper>
                            </Group>

                            {isOwnProfile && (
                                    <>
                                    <Divider mt="md" />
                                    <Stack mt="md">
                                        <Title order={5}>{t('alertPreferences.title')}</Title>
                                        <Text size="sm" c="dimmed">
                                            {t('alertPreferences.description')}
                                        </Text>
                                        <Select
                                            label={t('alertPreferences.minSeverity')}
                                            data={[
                                                { value: 'Low', label: t('alertPreferences.low') },
                                                { value: 'Moderate', label: t('alertPreferences.moderate') },
                                                { value: 'High', label: t('alertPreferences.high') },
                                            ]}
                                            value={userPreferences?.alertBellMinSeverity ?? 'Low'}
                                            onChange={(value) => {
                                                if (value) 
                                                    updateAlertBellMinSeverity.mutate(value);
d                                                
                                            }}
                                            maw={400}
                                        />
                                    </Stack>
                                    </>
                            )}
                            <Group mt="md">
                                <Button ml={'auto'} variant="default" onClick={handleCancel}>
                                    {t('button.cancel')}
                                </Button>
                                <Button loading={isSubmitting} type="submit">
                                    {t(params.id ? 'button.save' : 'button.save')}
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                    {error && (
                        <Notification color="red" onClose={() => setError(null)}>
                            {error}
                        </Notification>
                    )}
                </Paper>
                
            </>
        );
    }),
);

export default HcpForm;
